// ── session-lifecycle-modal: complete / cancel / archive confirm (Wave 6) ───
// POST /sessions/:id/lifecycle {status, notes}. Status ∈ completed|cancelled|
// archived. Cancel + archive are DESTRUCTIVE, so the confirm copy + the primary
// button reflect that (danger). The page owns the actual POST + toast (only after
// the backend confirms). A refused / closed write reads as its honest message
// inline and the dialog stays open — a closed write NEVER implies the transition
// happened (no-fabrication gate).

import { Alert, Button, Input, Modal } from "@vjal/ui";
import {  useState } from "react";

import { destructiveButtonClass } from "../../components/ui-classes";
import type { CoachingSessionDto, SessionLifecycleRequest } from "../../services/coaching-dtos";
import { describeSessionWriteError, type SessionLifecycleTransition } from "./sessions-helpers";

export interface SessionLifecycleModalProps {
  open: boolean;
  session: CoachingSessionDto | null;
  transition: SessionLifecycleTransition | null;
  onClose: () => void;
  onConfirm: (body: SessionLifecycleRequest) => Promise<unknown>;
  submitting?: boolean;
  submitError?: unknown;
}

export function SessionLifecycleModal({
  open,
  session,
  transition,
  onClose,
  onConfirm,
  submitting = false,
  submitError
}: SessionLifecycleModalProps) {
  const [notes, setNotes] = useState("");

  // Reset on open transition as a render adjustment (bug-pack class, 2026-07-06).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open && notes !== "") setNotes("");
  }

  const topError = describeSessionWriteError(submitError);
  const title = session?.title?.trim() ? session.title : "this session";
  const destructive = transition?.destructive ?? false;

  return (
    <Modal open={open} onClose={onClose} title={transition?.label ?? "Update session"}>
      <div className="flex flex-col gap-4">
        {topError && <Alert variant={topError.tone === "warning" ? "warning" : "error"}>{topError.message}</Alert>}
        <Alert variant={destructive ? "warning" : "info"}>
          {transition?.status === "completed"
            ? `Marking ${title} complete records the session as finished. It confirms before any change is made.`
            : transition?.status === "cancelled"
              ? `Cancelling ${title} stops its invites and future provider events. Past records are preserved. This confirms before any change.`
              : `Archiving ${title} hides it from active schedules. Records are preserved. This confirms before any change.`}
        </Alert>
        <Input
          label={destructive ? "Reason" : "Notes (optional)"}
          aria-label="Lifecycle notes"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
        <div className="mt-2 flex items-center justify-end gap-3">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={destructive ? "danger" : "primary"}
            className={destructive ? destructiveButtonClass : undefined}
            isLoading={submitting}
            disabled={!transition}
            onClick={() => {
              if (!transition) return;
              void onConfirm({
                status: transition.status,
                ...(notes.trim() ? { notes: notes.trim() } : {})
              }).catch(() => {
                // The owning mutation surfaces the error via submitError.
              });
            }}
          >
            {transition?.label ?? "Confirm"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
